<template>
  <div class="card-wrapper">
    <content-loader
      v-if="placeholder"
      :height="type === 'image' ? 286 : 360"
      :width="244"
      :speed="2"
      primaryColor="#f3f3f3"
      secondaryColor="#ecebeb"
      :style="{ marginBottom: 1.875 + 'rem' }"
    ></content-loader>
    <div v-else class="card mb-0 align-items-center">
      <router-link v-if="avatar" :to="'/art/' + artistUsername">
        <img
          class="artist-avatar rounded-circle"
          :src="avatar"
          :alt="username + 'avatar'"
        />
      </router-link>
      <div
        v-if="
          (proFile && !proFileSold && routeName !== 'My Images') ||
          (proFile && routeName === 'My Images' && proFileSold)
        "
        class="pro-file-icon"
      >
        <badge type="success">PRO</badge>
      </div>
      <base-button
        v-if="type !== 'animation' && !showPlayIcon"
        class="preview-icon"
        @click="showPreview()"
        aria-label="Preview artwork"
      >
        <i class="fas fa-eye"></i>
      </base-button>
      <router-link
        v-if="type === 'image'"
        :class="isDisabled ? 'image-link disabled' : 'image-link'"
        :to="getCardLink()"
        :aria-label="'View artwork ' + artworkId"
      >
        <div
          class="card-img-top"
          :style="{ 'background-image': 'url(' + thumbnail + ')' }"
        >
          <div v-if="licensed" class="licensed-overlay">
            <i class="fas fa-exclamation-triangle"></i>
            <div>THIS IMAGE IS NO LONGER AVAILABLE</div>
          </div>
          <div v-else-if="isDisabled" class="licensed-overlay">
            <i class="fas fa-exclamation-triangle"></i>
            <div>THIS ARTIST HAS BEEN DISABLED</div>
          </div>
          <i v-if="showPlayIcon" class="fas fa-play-circle" />
        </div>
      </router-link>
      <router-link
        v-if="type === 'animation'"
        class="image-link"
        :to="'/animation/' + artistUsername + '/' + artworkId"
        :aria-label="'View animation ' + artworkId"
      >
        <div class="video-wrapper">
          <div v-if="licensed" class="licensed-overlay">
            <i class="fas fa-exclamation-triangle"></i>
            <div>THIS ANIMATION IS NO LONGER AVAILABLE</div>
          </div>
          <div v-else-if="isDisabled" class="licensed-overlay">
            <i class="fas fa-exclamation-triangle"></i>
            <div>THIS ARTIST HAS BEEN DISABLED</div>
          </div>
          <i v-if="showPlay && !licensed" class="fas fa-play-circle" />
          <video
            class="video-container"
            ref="anim"
            loop
            width="100%"
            @mouseover="playVideo()"
            @mouseleave="pauseVideo()"
            preload="none"
            muted
            :poster="poster"
          >
            <source :src="thumbnail" type="video/mp4" />
          </video>
        </div>
      </router-link>
    </div>
    <div v-if="!placeholder && routeName === 'My Images'">
      <div class="d-flex license-details">
        <div class="w-100">
          {{
            termLicenseType === "6mo" ? "6 Month License" : "Forever License"
          }}
        </div>
        <div v-if="termLicenseType === '6mo'" class="text-right w-100">
          Expires: {{ termLicenseExpires }}
        </div>
      </div>
      <div class="d-flex">
        <base-button
          type="primary"
          size="sm"
          class="buy-button"
          block
          :loading="isLicenseDownloading"
          @click="handleLicenseClick"
          ><i class="fas fa-download"></i
          >{{ licenseDownloadButtonText }}</base-button
        >
        <base-button
          type="primary"
          size="sm"
          class="buy-button"
          block
          :loading="isDownloading"
          @click="handleClick"
          ><i class="fas fa-download"></i
          >{{ imageDownloadButtonText }}</base-button
        >
      </div>
      <div v-if="type !== 'animation'">
        <base-button
          type="primary"
          size="sm"
          class="buy-button"
          block
          @click="handleAddDesignService"
          >ADD DESIGN SERVICE</base-button
        >
      </div>
      <div class="mt-2 mb-5">
        <base-button
          type="success"
          size="md"
          :class="
            termLicenseType === '6mo' ? 'buy-button' : 'buy-button btn-hidden'
          "
          block
          :loading="isUpgrading"
          @click="handleUpgradeClick"
          :disabled="isInCart"
          >{{
            isInCart
              ? "ADDED TO CART"
              : `${
                  upgradePrice
                    ? `UPGRADE FOR $${upgradePrice}`
                    : "UNABLE TO UPGRADE"
                }`
          }}</base-button
        >
      </div>
    </div>
    <div
      v-if="!placeholder && routeName !== 'My Images'"
      class="artwork-card-footer"
    >
      <div class="footer-wrapper">
        <div v-if="showUsername" class="artist-handle">
          @{{ artistUsername }}
        </div>
        <div v-else class="price-wrapper">
          <strong v-if="price && !proFileOnly">${{ price }}</strong>
        </div>
        <verified-badge size="xs" v-if="isVerified" :showTooltip="false" />
        <div class="artwork-detail">
          <base-button
            v-if="
              onCollection &&
              isLoggedIn &&
              user.id > 0 &&
              user.id == $route.params.userId
            "
            class="action-button remove-from-collection"
            @click="handleRemoveFromCollection(artworkId)"
          >
            <i class="fa fa-trash-alt"></i>
          </base-button>
          <base-button
            v-else
            class="action-button add-to-collection"
            @click="handleAddToCollection(artworkId, poster)"
            aria-label="Add artwork to collection"
          >
            <i v-if="!isInCollection" class="far fa-heart"></i>
            <i v-else class="fa fa-heart"></i>
          </base-button>
        </div>
      </div>
      <div
        class="price-wrapper d-flex justify-content-between align-items-center"
      >
        <div v-if="showUsername">
          <strong v-if="price && !proFileOnly">${{ price }}</strong>
        </div>
        <div>
          <strong v-if="proFilePrice"
            ><span>PRO</span> ${{ proFilePrice }}</strong
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ContentLoader } from "vue-content-loader";
import { mapState } from "vuex";
import swal from "sweetalert2";
import VerifiedBadge from "./VerifiedBadge.vue";

const apiBaseURL = process.env.VUE_APP_API_URL;

export default {
  name: "artwork-card",
  data() {
    return {
      photo: "",
      filename: "",
      isDownloading: false,
      isLicenseDownloading: false,
      collectionId: null,
      downloadFileType: null,
      showPlay: true,
      isUpgrading: false,
    };
  },
  props: {
    thumbnail: {
      type: String,
      description: "Artwork image url",
    },
    avatar: {
      type: String,
      description: "Artist's avatar url",
    },
    artworkId: {
      type: String,
      description: "Artwork id",
    },
    username: {
      type: String,
      description: "Artist's username",
    },
    placeholder: {
      type: Boolean,
      default: false,
      description: "Toggle the card placeholder",
    },
    licensed: {
      type: Boolean,
      default: false,
      description: "Is the image licensed?",
    },
    proFile: {
      type: Boolean,
      default: false,
      description: "Is this a pro file?",
    },
    proFileSold: {
      type: Boolean,
      default: false,
      description: "Is this pro file sold?",
    },
    type: {
      type: String,
      default: "image",
      description: "Artwork type (image/animation)",
    },
    poster: {
      type: String,
      default: null,
      description: "Video poster url",
    },
    showPlayIcon: {
      type: Boolean,
      default: false,
      description: "Show a play icon for animations (image slider)",
    },
    price: {
      type: String,
      description: "Item price",
    },
    proFilePrice: {
      type: String,
      description: "PRO file price",
    },
    proFileOnly: {
      type: Boolean,
    },
    showUsername: {
      type: Boolean,
      default: true,
      description: "Option to hide username (for artist profile)",
    },
    isVerified: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    termLicenseStatus: {
      type: String,
    },
    termLicenseCreated: {
      type: String,
    },
    termLicenseType: {
      type: String,
    },
    termLicenseExpires: {
      type: String,
    },
  },
  components: {
    ContentLoader,
    VerifiedBadge,
  },
  mounted() {
    if (
      this.$route.params.collectionId &&
      this.$route.params.collectionId > 0
    ) {
      this.collectionId = this.$route.params.collectionId;
    }
  },
  computed: {
    ...mapState(["isLoggedIn", "user", "stripe"]),
    isInCart() {
      if (this.stripe && this.stripe.cartItems.length) {
        const item = this.stripe.cartItems.find(
          (c) => c.artwork_id === this.artworkId && !c.design_service
        );
        if (item) return true;
      }
      return false;
    },
    upgradePrice() {
      if (this.termLicenseType === "6mo") {
        if (this.proFile) {
          return (parseFloat(this.proFilePrice) - 20.0).toFixed(2);
        } else {
          return (parseFloat(this.price) - 20.0).toFixed(2);
        }
      }
      return null;
    },
    claimed() {
      if (this.type === "image") {
        return this.user.claimedImages.find(
          (image) => image.id === this.artworkId
        );
      }
      if (this.type === "animation") {
        return this.user.claimedAnimations.find(
          (anim) => anim.id === this.artworkId
        );
      }
      return false;
    },
    onCollection() {
      if (this.$route.params.collectionId > 0) {
        return true;
      }
      return false;
    },
    routeName() {
      return this.$route.name;
    },
    artistUsername() {
      if (this.username) {
        return this.username.replace("@", "");
      }
      return null;
    },
    imageDownloadButtonText() {
      if (this.isLoggedIn) {
        if (this.claimed) {
          let downloadText = "IMAGE";
          if (this.proFileSold) downloadText = "PSD";
          if (this.type === "animation") downloadText = "MP4";
          return this.isDownloading ? "" : downloadText;
        } else {
          return "CLAIM NOW";
        }
      }
      return "BUY NOW";
    },
    licenseDownloadButtonText() {
      if (this.isLoggedIn) {
        if (this.claimed) {
          return this.isLicenseDownloading ? "" : "LICENSE";
        } else {
          return "CLAIM NOW";
        }
      }
      return "BUY NOW";
    },
    isInCollection() {
      if (this.user.collections.length > 0) {
        return this.findDeep(this.user.collections, this.artworkId);
      }
      return false;
    },
  },
  methods: {
    handleAddDesignService() {
      const data = {
        id: this.artworkId,
        thumbnail: this.thumbnail,
        type: this.proFile ? "pro" : this.type,
        artist: this.artistUsername,
      };
      this.$store.commit("setLastAddedToCart", data);
      this.$store.commit("showDesignServiceModal", true);
    },
    handleUpgradeClick() {
      swal
        .fire({
          title: "ARE YOU SURE?",
          text: `Are you sure you want to upgrade this ${this.type} to a forever license?`,
          type: "warning",
          showCancelButton: true,
          cancelButtonText: "NO",
          confirmButtonColor: "#000",
          cancelButtonColor: "#f5365c",
          confirmButtonText: "YES",
        })
        .then((result) => {
          if (result.value) {
            this.axios
              .request({
                url: `${apiBaseURL}/api/artv1.1/user/addtocart`,
                method: "post",
                data: {
                  artwork_id: this.artworkId,
                  type: this.proFile ? "pro" : this.type,
                  license: "forever",
                  thumbnail:
                    this.type === "animation" ? this.poster : this.thumbnail,
                  artist: this.artistUsername,
                  is_upgrade: 1,
                },
              })
              .then((result) => {
                if (result.data.data.message === "Success") {
                  this.$store.dispatch("getCart");
                  swal
                    .fire({
                      type: "success",
                      title: "Success!",
                      text: "The upgrade has been added to your cart. After checkout is completed and your payment is confirmed, the license will be upgraded.",
                      showCancelButton: true,
                      cancelButtonText: "CLOSE",
                      confirmButtonColor: "#000",
                      cancelButtonColor: "#f5365c",
                      confirmButtonText: "CHECKOUT NOW",
                    })
                    .then((result) => {
                      if (result.value) {
                        this.$router.push({ path: "/checkout" });
                      }
                    });
                } else {
                  this.showErrorAlert(
                    "There was a problem adding the upgrade to your cart. Please try again or email contact@artgrab.co for support."
                  );
                }
              })
              .catch((error) => {
                /*eslint no-console: ["error", { allow: ["warn", "error"] }] */
                this.showErrorAlert(
                  "There was a problem adding the upgrade to your cart. Please try again or email contact@artgrab.co for support."
                );
                console.error(error);
              });
          }
        });
    },
    showPreview() {
      this.$store.commit("setPreviewImage", this.thumbnail);
      this.$store.commit("showPreviewsModal", true);
    },
    getCardLink() {
      if (this.showPlayIcon) {
        return "/animation/" + this.artistUsername + "/" + this.artworkId;
      } else {
        return "/art/" + this.artistUsername + "/" + this.artworkId;
      }
    },
    playVideo() {
      this.$refs.anim.play();
      this.showPlay = false;
    },
    pauseVideo() {
      this.showPlay = true;
      this.$refs.anim.pause();
    },
    handleAddToCollection(photo_id, sized_url) {
      const assetType =
        this.showPlayIcon || this.type === "animation" ? "animation" : "image";
      this.$store.commit("showAddToCollectionModal", {
        status: true,
        photo_id: photo_id,
        sized_url: sized_url,
        type: assetType,
      });
    },
    findDeep(collections, photo_id) {
      const self = this;
      if (this.type === "animation" || this.showPlayIcon) {
        return collections.some(function (e) {
          if (e.animation_id == photo_id) {
            return true;
          } else if (e.animations) {
            return self.findDeep(e.animations, photo_id);
          }
        });
      } else {
        return collections.some(function (e) {
          if (e.photo_id == photo_id) {
            return true;
          } else if (e.photos) {
            return self.findDeep(e.photos, photo_id);
          }
        });
      }
    },
    handleRemoveFromCollection(photo_id) {
      swal
        .fire({
          title: "ARE YOU SURE?",
          text: "Are you sure you want to remove this artwork from your collection?",
          type: "warning",
          showCancelButton: true,
          confirmButtonColor: "#000",
          cancelButtonColor: "#f5365c",
          confirmButtonText: "YES, REMOVE IT",
        })
        .then((result) => {
          if (result.value) {
            this.axios
              .request({
                url: `${apiBaseURL}/api/artv1.1/user/removeFromCollection`,
                method: "post",
                data: {
                  collection_id: this.collectionId,
                  photo_id: photo_id,
                  type: this.type,
                },
              })
              .then((result) => {
                if (result.data.data.message === "Success") {
                  this.$emit("imageRemovedFromCollection");
                  this.$store.dispatch("getCollections");
                }
              })
              .catch((error) => {
                /*eslint no-console: ["error", { allow: ["warn", "error"] }] */
                console.error(error);
              });
          }
        });
    },
    handleClick: function (photo_id, image_url, username, pro_file) {
      if (this.isLoggedIn && this.claimed) {
        this.checkDownload();
      } else {
        this.$router.push({
          path: "/art/" + username + "/" + photo_id,
        });
      }
    },
    handleLicenseClick: function (photo_id, image_url, username, pro_file) {
      if (this.isLoggedIn && this.claimed) {
        this.checkDownload("license");
      } else {
        this.$router.push({
          path: "/art/" + username + "/" + photo_id,
        });
      }
    },
    showErrorAlert(message) {
      swal.fire({
        type: "error",
        title: "Oh no!",
        text: message,
        confirmButtonColor: "#000",
      });
    },
    showSuccessAlert(message) {
      swal.fire({
        type: "success",
        title: "Success!",
        text: message,
        confirmButtonColor: "#000",
        onClose: this.closeModal,
      });
    },
    checkDownload(type) {
      if (
        type === "license" &&
        this.termLicenseCreated &&
        this.termLicenseStatus !== "upgraded"
      ) {
        this.downloadLicense(true);
      } else if (this.artworkId > 0) {
        this.axios
          .request({
            baseURL: apiBaseURL,
            url: "/api/artv1.1/photo/claimedinfo",
            method: "post",
            data: {
              id: this.artworkId,
              is_pro_file: this.proFileSold,
              is_animation: this.type === "animation",
            },
          })
          .then((result) => {
            if (
              result.data.error_code == 0 &&
              result.data.message == "Success"
            ) {
              if (result.data.data.message == "Success") {
                this.photo = result.data.data.photo;
                let fileName = "file.jpg";

                if (type === "license") {
                  fileName = `Art-Grab-Exclusive-Licensing-Agreement-${this.artworkId}.pdf`;
                }
                this.filename = fileName;

                if (type === "license") {
                  this.downloadLicense();
                } else {
                  this.download();
                }
              } else {
                this.showErrorAlert(
                  "We're sorry, there was an error getting information for this file. Please try again or contact us for support."
                );
              }
            } else {
              this.showErrorAlert(
                "We're sorry, there was an error getting information for this file. Please try again or contact us for support."
              );
            }
          })
          .catch((error) => {
            /*eslint no-console: ["error", { allow: ["warn", "error"] }] */
            console.error(error);
            this.showErrorAlert(
              "We're sorry, there was an error getting information for this file. Please try again or contact us for support."
            );
          });
      } else {
        this.showErrorAlert(
          "We're sorry, there was an error getting information for this file. Please try again or contact us for support."
        );
      }
    },
    downloadLicense(is_term) {
      this.isLicenseDownloading = true;
      this.axios
        .request({
          baseURL: apiBaseURL,
          url: is_term
            ? "/api/artv1.1/photo/downloadtermlicense"
            : "/api/artv1.1/photo/downloadlicense",
          responseType: "blob",
          method: "post",
          data: {
            id: this.artworkId,
            is_animation: this.type === "animation",
            is_pro_file: this.proFileSold,
          },
        })
        .then((result) => {
          this.isLicenseDownloading = false;
          var filename = is_term
            ? `Art-Grab-Term-Licensing-Agreement-${this.artworkId}.pdf`
            : this.filename;
          var blob = new Blob([result.data], { type: "application/pdf" });

          if (typeof window.navigator.msSaveBlob !== "undefined") {
            // IE workaround for "HTML7007: One or more blob URLs were
            // revoked by closing the blob for which they were created.
            // These URLs will no longer resolve as the data backing
            // the URL has been freed."
            window.navigator.msSaveBlob(blob, filename);
          } else {
            var blobURL = window.URL.createObjectURL(blob);
            var tempLink = document.createElement("a");
            tempLink.style.display = "none";
            tempLink.href = blobURL;
            tempLink.setAttribute("download", filename);

            // Safari thinks _blank anchor are pop ups. We only want to set _blank
            // target if the browser does not support the HTML5 download attribute.
            // This allows you to download files in desktop safari if pop up blocking
            // is enabled.
            if (typeof tempLink.download === "undefined") {
              tempLink.setAttribute("target", "_blank");
            }

            document.body.appendChild(tempLink);
            tempLink.click();
            document.body.removeChild(tempLink);
            window.URL.revokeObjectURL(blobURL);
          }
        })
        .catch((error) => {
          this.isLicenseDownloading = false;
          swal.fire({
            type: "error",
            title: "Oh no!",
            text: "Could not download the license file. Please try again or contact us for support. ",
            confirmButtonColor: "#000",
          });
        });
    },
    async loadFile(url) {
      const blob = await fetch(url).then((r) => r.blob());
      const downloadFileName = `${this.artworkId}${
        this.proFileSold ? "-PRO" : ""
      }.${this.downloadFileType}`;
      try {
        if (typeof window.navigator.msSaveBlob !== "undefined") {
          // IE workaround for "HTML7007: One or more blob URLs were
          // revoked by closing the blob for which they were created.
          // These URLs will no longer resolve as the data backing
          // the URL has been freed."
          window.navigator.msSaveBlob(blob, downloadFileName);
        } else {
          var blobURL = window.URL.createObjectURL(blob);
          var tempLink = document.createElement("a");
          tempLink.style.display = "none";
          tempLink.href = blobURL;
          tempLink.setAttribute("download", downloadFileName);

          // Safari thinks _blank anchor are pop ups. We only want to set _blank
          // target if the browser does not support the HTML5 download attribute.
          // This allows you to download files in desktop safari if pop up blocking
          // is enabled.
          if (typeof tempLink.download === "undefined") {
            tempLink.setAttribute("target", "_blank");
          }

          document.body.appendChild(tempLink);
          tempLink.click();
          document.body.removeChild(tempLink);
          window.URL.revokeObjectURL(blobURL);
          this.isDownloading = false;
        }
      } catch (e) {
        this.isDownloading = false;
        swal.fire({
          type: "error",
          title: "Oh no!",
          text: `Could not download the file. Please try again or contact us for support. Error: ${e}`,
          confirmButtonColor: "#000",
        });
      }
    },
    download() {
      this.isDownloading = true;
      this.axios
        .request({
          baseURL: apiBaseURL,
          url: "/api/artv1.1/photo/download",
          method: "post",
          data: {
            id: this.artworkId,
            is_animation: this.type === "animation",
            is_pro_file: this.proFileSold,
          },
        })
        .then((result) => {
          this.downloadFileType = result.data.data.download_file_type;
          this.loadFile(result.data.data.download_url);
        })
        .catch((error) => {
          this.isDownloading = false;
          swal.fire({
            type: "error",
            title: "Oh no!",
            text: "Could not download the image file. Please try again or contact us for support. ",
            confirmButtonColor: "#000",
          });
        });
    },
  },
};
</script>

<style scoped lang="scss">
.card {
  box-shadow: none;
}

.card-img-top {
  padding-top: 100%;
  background-position: center center;
  background-size: cover;
  background-color: #dedede;
  border-radius: 4px;
  cursor: pointer;
}

.licensed-overlay {
  background-color: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 20px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 4px;
  width: 90%;
  text-align: center;

  i {
    font-size: 36px;
    display: block;
    margin-bottom: 12px;
  }
}

.preview-icon {
  position: absolute;
  bottom: 6px;
  left: 8px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  padding: 5px 3px;
  border-radius: 6px;

  &:focus,
  &:active {
    background: black !important;
    color: white !important;
  }
}

.pro-file-icon {
  position: absolute;
  top: 6px;
  right: 8px;
  pointer-events: none;
}

.disabled {
  pointer-events: none;
}

.image-link {
  width: 100%;
  height: 100%;

  .video-container {
    border-radius: 4px;
    object-fit: contain;

    video {
      border-radius: 4px;
    }
  }
}

.price-wrapper {
  span {
    color: #ccc;
  }
}

.fa-play-circle {
  font-size: 50px;
  z-index: 2;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.buy-button {
  margin-top: 0.25rem;
}

.btn-hidden {
  visibility: hidden;
}

.license-details {
  margin-top: 4px;
  font-size: 0.75rem;
  width: 100%;
}

.buy-now-button {
  position: absolute;
  bottom: 12px;
  right: 12px;
}

.action-button {
  padding: 0;
  background: none;
  border: 0;
  box-shadow: none;

  &.remove-from-collection {
    i {
      color: #000;
      font-size: 16px;
    }
  }

  &.add-to-collection {
    i {
      font-size: 18px;
      color: #000;
      &:hover {
        color: #f5365c;
      }
    }
  }

  &:active {
    background: none;

    &.add-to-collection {
      i {
        color: lighten(#f5365c, 8%);
      }
    }
  }
}

.artwork-card-footer {
  display: flex;
  flex-direction: column;
  padding: 0.55rem 0 0;
  width: 100%;
  margin-bottom: 4.5rem;

  .footer-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .artist-handle {
    font-size: 0.75rem;
    color: #000;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 2px;
  }

  .artwork-detail {
    font-size: 0.75rem;
  }
}

.artist-avatar {
  width: 50px;
  height: 50px;
  position: absolute;
  left: 15px;
  margin-top: -25px;
  border: 2px solid white;
  z-index: 1;
}

@media (max-width: 1399.98px) {
  .artist-avatar {
    width: 36px;
    height: 36px;
    left: 10px;
    margin-top: -18px;
    border: 1px solid white;
  }
}

@media (max-width: 991.98px) {
  .artwork-card-footer {
    margin-bottom: 0;

    .artwork-detail {
      .coin-icon {
        width: 12px;
      }
    }
  }

  .licensed-overlay {
    font-size: 0.65rem;

    i {
      font-size: 24px;
    }
  }

  .buy-button {
    margin-bottom: 0;
    font-size: 0.75rem;
  }

  .card-wrapper {
    margin-bottom: 2.75rem;
  }
}

@media (max-width: 575.98px) {
  .price-wrapper {
    font-size: 0.8rem;
    margin-top: 4px;
  }
}
</style>
